/**
 * @fileoverview ApplicationPortalHost — stateful orchestrator that wires the
 * AppPortalDialog primitives to the IronCase app catalog. Owns the
 * selected-category (nav highlight + scroll target), selected-app (details
 * panel), and search-query state, and composes the dialog out of:
 *
 *   AppPortalDialog
 *   ├─ AppPortalSearchHeader              (search slot, above results)
 *   ├─ AppPortalNavigation                (left rail; category list)
 *   │   └─ AppPortalNavigationItem ×N      (clicking scrolls to that section)
 *   ├─ AppPortalResultList ×N             (one headed list per category, all
 *   │   └─ AppCard ×N                       shown at once and scrollable)
 *   └─ AppPortalDetailsPanel              (right column; selected app)
 *
 * The result column shows every category grouped under its own heading
 * (Foundry "all apps" view); the left rail jumps between sections rather
 * than swapping the visible set. Search narrows cards across all groups and
 * hides groups that end up empty.
 *
 * Live entries navigate via react-router's `useNavigate()`. Coming-soon
 * entries render as visually-disabled cards (opacity + pointer-events: none).
 */

import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
/*
  `IconName` is narrowed back HERE, at the render edge. The port carries a plain
  string because a port must not name Blueprint (hex-boundaries.test.ts), and the
  catalog module still types its entries as `IconName` — so the guarantee exists
  at both ends and only the wire between them is neutral.
*/
import { NonIdealState, type IconName } from "@blueprintjs/core";
import { useNavigate } from "react-router";

import { useFavoriteApps } from "../../lib/favorite-apps";
import { useRecentApps } from "../../lib/recent-apps";
import { AppCard } from "./app-card";
import { container } from "../../hex/container";
import type { AppCatalog, AppCatalogPort, CatalogApp } from "../../hex/ports/app-catalog";
import { AppPortalDetailsPanel } from "./app-portal-details-panel";
import { AppPortalDialog } from "./app-portal-dialog";
import { AppPortalCategoryHeader } from "./app-portal-category-header";
import { AppPortalNavigation } from "./app-portal-navigation";
import { AppPortalNavigationItem } from "./app-portal-navigation-item";
import { AppPortalResultList } from "./app-portal-result-list";
import { AppPortalSearchHeader } from "./app-portal-search-header";

/**
 * The unfiltered view. A sentinel rather than a category, because it is not one —
 * it is the absence of a category filter, and giving it a name that could collide
 * with a real category is how "All apps" would one day start hiding a group.
 */
const ALL_APPS = "\u0000all-apps";

/** The starred group. A sentinel for the same reason as ALL_APPS. */
const PROMOTED = "\u0000promoted";

interface ApplicationPortalHostProps {
  open: boolean;
  onClose: () => void;
  /**
   * Where the catalog comes from. Defaults to the container's, so callers need
   * not know; a test supplies its own without touching the composition root.
   */
  catalogPort?: AppCatalogPort;
}

/** Case-insensitive substring match across title + description. */
function matchesQuery(entry: CatalogApp, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return entry.title.toLowerCase().includes(q) || entry.description.toLowerCase().includes(q);
}

export function ApplicationPortalHost({ open, onClose, catalogPort }: ApplicationPortalHostProps) {
  const navigate = useNavigate();
  /*
    THE CATALOG COMES THROUGH A PORT. This screen used to import
    IRONCASE_APP_CATALOG directly, welding it to one hard-coded array — it could
    not be served from the API, gated per agency, or stubbed in a test without
    editing the component. The rest of the app already draws that line
    (hex/ports/{auth,http}); the launcher was the exception. The shape is
    foundry-ui's `app-launcher` port, so a swap in either direction is an adapter.

    `load()` may be sync or async; both resolve through the same path so an
    adapter is never forced to match the fixture's shape rather than the
    interface.
  */
  const port = catalogPort ?? container().appCatalog;
  const [catalog, setCatalog] = useState<AppCatalog | null>(null);
  useEffect(() => {
    let live = true;
    Promise.resolve(port.load()).then((c) => { if (live) setCatalog(c); });
    return () => { live = false; };
  }, [port]);
  const { recordLaunch } = useRecentApps();
  const { favorites, isFavorite, toggleFavorite } = useFavoriteApps();

  /*
    Opens on ALL APPS, as the capture does — its rail shows "All apps" selected
    and the full list below. Selecting a category on open would filter the
    launcher before the user has asked for anything.
  */
  const [selectedCategory, setSelectedCategory] = useState<string>(ALL_APPS);
  const [searchQuery, setSearchQuery] = useState<string>("");

  /*
    NOTHING IS SELECTED ON OPEN, which is what the captured launcher does.

    This used to pre-select the first app of the default category, so the right
    pane always showed an application and the empty state was unreachable. The
    reference PNG (foundry-ui: foundry-overlays-applications-launcher) opens on
    "Click on an application to see details" — the pane is a RESPONSE to a click,
    and pre-filling it both invents a selection the user did not make and asserts
    that one app is more interesting than the rest.
  */
  const [selectedAppId, setSelectedAppId] = useState<string>("");

  // Section wrappers keyed by category, so a nav click can scroll to one.
  const sectionRefs = useRef(new Map<string, HTMLDivElement>());

  /*
    The rail's counts come from the CATALOG, not from counting the rows on
    screen. The port says the count is the catalog's to report, because a
    catalog that gates or pages rows still shows the full figure.
  */
  const categoryCounts = useMemo(
    () => new Map((catalog?.categories ?? []).map((c) => [c.id, c.count])),
    [catalog],
  );
  const categories = catalog?.categories ?? [];
  const allApps = catalog?.apps ?? [];

  // Every category as a headed group, filtered by search. Empty groups drop
  // out so a search doesn't leave dangling headings.
  const groups = useMemo(() => {
    /*
      Selecting "Starred" narrows the list to the promoted apps. Without this the
      rail entry would scroll to a section that does not exist and silently do
      nothing — a control that looks like it works, which is worse than one that
      is visibly absent.
    */
    const inScope = (e: CatalogApp) =>
      selectedCategory === PROMOTED ? favorites.has(e.id) : true;
    return categories
      .map(({ id, label }) => ({
        category: label,
        apps: allApps.filter(
          (e) => e.category === id && inScope(e) && matchesQuery(e, searchQuery)
        ),
      }))
      .filter((g) => g.apps.length > 0);
  }, [searchQuery, selectedCategory, favorites, categories, allApps]);

  // Resolve the right-pane app — fall back to the first visible entry if the
  // current selection got filtered out by search.
  const selectedEntry: CatalogApp | undefined = useMemo(() => {
    // No selection yet — the dialog's opening state. The fallback below is for a
    // selection that SEARCH filtered away, not for having never chosen one.
    if (!selectedAppId) return undefined;
    const byId = allApps.find((e) => e.id === selectedAppId);
    const stillVisible = groups.some((g) => g.apps.some((e) => e.id === selectedAppId));
    if (byId && stillVisible) return byId;
    return groups[0]?.apps[0] ?? byId;
  }, [groups, selectedAppId, allApps]);

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    if (category === ALL_APPS) {
      // No section to scroll to — "All apps" is the unfiltered view, so it
      // returns to the top rather than jumping to a heading that is not there.
      sectionRefs.current.values().next().value?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    sectionRefs.current.get(category)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCardClick = (entry: CatalogApp) => (e: MouseEvent<HTMLAnchorElement>) => {
    /*
      A CLICK SELECTS. IT DOES NOT LAUNCH.

      This used to navigate and close the dialog on a live entry, which made the
      detail pane unreachable — you could never see an application's details,
      because choosing one took you away from the launcher. The parity gate found
      it by clicking a row and finding no pane on our side at all.

      Foundry's launcher selects: the empty pane says "Click on an application to
      see details", the captured pane carries the app's title, metadata and its
      own Open action, and launching is that second, deliberate click. Our own
      empty state already promised the same thing and the code contradicted it.

      preventDefault on every path: the AppCard is wrapped in an <a> so it is
      keyboard-reachable and shows a real href on hover, and the anchor must not
      navigate out from under the selection.
    */
    e.preventDefault();
    setSelectedAppId(entry.id);
    setSelectedCategory(entry.category);
  };

  /** Launch from the detail pane — the deliberate second click. */
  const handleOpen = (entry: CatalogApp) => () => {
    // No href means the app is not built. The pane does not offer Open in that
    // case, so this is unreachable — but a guard beats navigating to undefined.
    if (!entry.href) return;
    recordLaunch(entry.id);
    onClose();
    navigate(entry.href);
  };


  return (
    <AppPortalDialog
      isOpen={open}
      onClose={onClose}
      search={
        <AppPortalSearchHeader
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onClose={onClose}
        />
      }
      navigation={
        <AppPortalNavigation>
          {/*
            "All apps" FIRST, then the group label, then the categories — the
            composition the live capture has. Its nav reads
            ["All apps", …9 categories…, "assets"] with an uppercase group label
            above the category block.

            Ours had neither: the rail opened straight into the categories, so
            there was no way back to the unfiltered list once a category was
            chosen, and the categories floated with nothing naming what they
            were. I previously reported these as present on the strength of a
            grep that matched nothing real — the parity gate against the capture
            is what actually found it.
          */}
          <AppPortalNavigationItem
            label="All apps"
            count={catalog?.total ?? 0}
            selected={selectedCategory === ALL_APPS}
            onClick={() => handleCategoryClick(ALL_APPS)}
          />
          <AppPortalCategoryHeader title="Platform apps" />
          {categories.map(({ id: cat }) => (
            <AppPortalNavigationItem
              key={cat}
              label={cat}
              count={categoryCounts.get(cat) ?? 0}
              selected={cat === selectedCategory}
              onClick={() => handleCategoryClick(cat)}
            />
          ))}
          {/*
            The SECOND rail group. Foundry's launcher has two — "Platform apps"
            and "Promoted apps" — and the parity gate counts them, because having
            somewhere to promote into is chrome even though WHAT is promoted is
            data.

            Ours is the starred apps. The stars were already rendered on every
            row and wired to nothing, so favouriting an app did nothing and the
            rail had nowhere to show the result; `useFavoriteApps` was sitting
            unused. This connects both ends.
          */}
          <AppPortalCategoryHeader title="Promoted apps" />
          <AppPortalNavigationItem
            label="Starred"
            count={favorites.size}
            selected={selectedCategory === PROMOTED}
            onClick={() => handleCategoryClick(PROMOTED)}
          />
        </AppPortalNavigation>
      }
      results={
        groups.length === 0 ? (
          <div className="bp6-text-muted" style={{ padding: "20px 24px" }}>
            No applications match "{searchQuery}".
          </div>
        ) : (
          /*
            NO WRAPPER ELEMENTS. Foundry's section is
            `__list__ > [h5, a.__container__ …, divider]` — the anchors are
            direct children. Ours had a `div` around each list (holding the
            scroll-anchor ref) and another around each card (holding the click
            handler), which put every row two levels deeper than its counterpart
            and made the whole subtree compare against the wrong nodes. Both jobs
            moved onto the elements that were already there.

            NO ROW-WIDE OPACITY either. The disabled state was `opacity: 0.5` on
            the whole row, and 17 of the 22 catalogue entries are coming-soon, so
            more than three quarters of the launcher rendered at half strength —
            grey text on a dark panel, which reads as broken rather than as
            partly unavailable. The icon dims, the title goes muted, the cursor
            says no. See `.bp-app-portal-card-disabled`.
          */
          groups.map(({ category, apps }, idx) => (
            <AppPortalResultList
              key={category}
              title={category}
              withTrailingDivider={idx < groups.length - 1}
              sectionRef={(el) => {
                if (el) sectionRefs.current.set(category, el);
                else sectionRefs.current.delete(category);
              }}
            >
              {apps.map((entry) => {
                const isComingSoon = entry.status === "coming-soon";
                return (
                  <AppCard
                    key={entry.id}
                    icon={entry.icon as IconName}
                    iconColor={entry.color}
                    favorited={isFavorite(entry.id)}
                    onToggleFavorite={() => toggleFavorite(entry.id)}
                    title={entry.title}
                    description={
                      isComingSoon ? `${entry.description} (Coming soon)` : entry.description
                    }
                    href={entry.href ?? "#"}
                    comingSoon={isComingSoon}
                    onClick={handleCardClick(entry)}
                    selected={entry.id === selectedAppId}
                  />
                );
              })}
            </AppPortalResultList>
          ))
        )
      }
      detailsPanel={
        selectedEntry ? (
          <AppPortalDetailsPanel
            icon={selectedEntry.icon as IconName}
            title={selectedEntry.title}
            description={
              selectedEntry.status === "coming-soon"
                ? `${selectedEntry.description} (Coming soon)`
                : selectedEntry.description
            }
            openHref={selectedEntry.href ?? "#"}
            onOpen={selectedEntry.href ? handleOpen(selectedEntry) : undefined}
          />
        ) : (
          /*
            The empty state, from the captured launcher rather than invented.
            IronCase rendered NOTHING here — a blank third column that reads as a
            layout bug, and gives no clue that clicking a row fills it.

            Foundry's is a Blueprint NonIdealState with the `select` icon and one
            line of text in the DESCRIPTION slot: the capture's markup is
            `bp6-non-ideal-state-visual > bp6-icon-select bp6-icon-muted` then
            `bp6-non-ideal-state-text > div`, with no `bp6-heading` — so a title
            would be a shape the real dialog does not have.
            (foundry-ui: foundry-overlays-applications-launcher, verbatim
            application-launcher.html.)
          */
          <NonIdealState
            data-testid="app-portal-details-empty"
            icon="select"
            description="Click on an application to see details"
          />
        )
      }
    />
  );
}
